import { FiMic } from 'react-icons/fi';

export const basicPronunciationData = {
  courseId: 'basic-pronunciation',
  courseType: 'speaking-course',
  title: 'Basic Pronunciation',
  icon: FiMic,
  overview: {
    impact: 'Master the English sounds, stress, and intonation that examiners listen for',
    focus: 'Individual sounds, word stress, sentence stress, and intonation patterns',
    uses: 'Pronunciation score (25% of Speaking)',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Key English Sounds',
      content: `<p>Several English sounds don't exist in many other languages. Mastering them is the fastest way to sound clearer.</p>`,
      points: [
        '<strong>/θ/ and /ð/ (th):</strong> Place your tongue between your teeth. "Think" uses voiceless /θ/; "this" uses voiced /ð/. Don\'t replace them with /s/, /z/, /t/, or /d/.',
        '<strong>/r/:</strong> Curl the tip of your tongue back without touching the roof of your mouth. It is different from the rolled "r" in Spanish or French.',
        '<strong>/l/:</strong> Touch the tip of your tongue to the ridge behind your upper teeth. Distinguish "light" /l/ (beginning of words) from "dark" /l/ (end of words, e.g., "full").',
        '<strong>/v/ vs /w/:</strong> For /v/, your upper teeth touch your lower lip. For /w/, round your lips without teeth contact. Compare: "vest" vs "west".',
        '<strong>/æ/ vs /e/:</strong> Open your mouth wider for /æ/ ("cat") than for /e/ ("bed").',
      ],
      table: {
        headers: ['Sound', 'Example Word', 'Common Mistake'],
        rows: [
          ['/θ/ (voiceless th)', 'think, three, bath', 'Saying "sink" or "tink"'],
          ['/ð/ (voiced th)', 'this, the, mother', 'Saying "zis" or "dis"'],
          ['/r/', 'red, very, car', 'Rolling or trilling the r'],
          ['/v/ vs /w/', 'vine vs wine', 'Swapping the two sounds'],
          ['/l/', 'light, full', 'Dropping final "l" sound'],
        ],
      },
      warning: 'You don\'t need a perfect accent. The goal is to be clearly understood.',
    },
    {
      title: 'Word Stress Patterns',
      content: `<p>English words have stressed and unstressed syllables. Putting stress on the wrong syllable can make you hard to understand—even if every sound is correct.</p>`,
      points: [
        '<strong>Two-syllable nouns</strong> usually stress the first syllable: <em>PRE</em>sent, <em>TA</em>ble, <em>PIC</em>ture.',
        '<strong>Two-syllable verbs</strong> usually stress the second syllable: pre<em>SENT</em>, re<em>CORD</em>, ob<em>JECT</em>.',
        '<strong>Same word, different stress:</strong> "I have a <em>PRE</em>sent" (noun) vs "I will pre<em>SENT</em> my work" (verb).',
        '<strong>Three-syllable words</strong> vary—check a dictionary for the pattern: <em>BEAU</em>tiful, im<em>POR</em>tant, under<em>STAND</em>.',
        'Suffixes like <em>-tion</em>, <em>-sion</em>, <em>-ic</em> pull stress to the syllable before them: eduCA<em>TION</em>, fanTAS<em>TIC</em>.',
      ],
      examples: [
        { label: 'Noun vs Verb', text: '"Can I have a REcord?" (noun) vs "I want to reCORD this." (verb)' },
        { label: 'Suffix rule', text: '"PHOtograph → phoTOGraphy → photoGRAPHic" — stress shifts as the suffix changes.' },
      ],
    },
    {
      title: 'Sentence Stress and Rhythm',
      content: `<p>English is a <strong>stress-timed</strong> language: stressed syllables occur at roughly equal intervals, and unstressed words are squeezed between them. This gives English its characteristic rhythm.</p>`,
      points: [
        '<strong>Content words</strong> (nouns, main verbs, adjectives, adverbs) are stressed: "I BOUGHT a NEW CAR YESTERDAY."',
        '<strong>Function words</strong> (articles, prepositions, pronouns, auxiliary verbs) are usually unstressed and spoken quickly: "I went <em>to the</em> shop."',
        'Speakers can shift stress for emphasis: "I didn\'t say HE stole it" (someone else did) vs "I didn\'t say he STOLE it" (he just borrowed it).',
        'Unstressed syllables often use the schwa /ə/ sound: "banana" → /bəˈnænə/.',
      ],
      warning: 'Stressing every word equally makes your speech sound robotic. Let the unimportant words be quick and soft.',
    },
    {
      title: 'Intonation Patterns',
      content: `<p>Intonation is the rise and fall of your voice. Using the wrong pattern can change your meaning or confuse the listener.</p>`,
      points: [
        '<strong>Falling intonation ↘:</strong> Use for statements, commands, and wh-questions. "Where do you LIVE↘"',
        '<strong>Rising intonation ↗:</strong> Use for yes/no questions and to show surprise or disbelief. "You live in PARIS↗?"',
        '<strong>Fall-rise ↘↗:</strong> Shows uncertainty, contrast, or that you haven\'t finished speaking. "I LIKE it↘... but it\'s exPENsive↗."',
        '<strong>List intonation:</strong> Items rise ↗, and the last item falls ↘: "I bought apples↗, oranges↗, and bananas↘."',
      ],
      examples: [
        { label: 'Statement', text: '"I come from Brazil↘." — falling = confident and complete.' },
        { label: 'Yes/No question', text: '"Do you like coffee↗?" — rising = expecting a yes or no.' },
        { label: 'Unfinished thought', text: '"Well↘↗..." — fall-rise = I have more to say.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify Correct Pronunciation',
      type: 'select',
      instructions: 'Choose the option that shows the correct English pronunciation.',
      questions: [
        {
          text: 'How is the "th" in "three" pronounced?',
          options: [
            'Tongue between teeth, no voice (like blowing air)',
            'Like the letter "t"',
            'Like the letter "s"',
          ],
          answer: 'Tongue between teeth, no voice (like blowing air)',
          explanation: '"Three" uses the voiceless /θ/. Place your tongue between your teeth and blow air out gently.',
        },
        {
          text: 'What is the difference between "vine" and "wine"?',
          options: [
            '"vine" uses teeth on lip (/v/); "wine" uses rounded lips (/w/)',
            'They are pronounced the same way',
            '"vine" starts with /w/ and "wine" starts with /v/',
          ],
          answer: '"vine" uses teeth on lip (/v/); "wine" uses rounded lips (/w/)',
          explanation: 'For /v/, your upper teeth touch your lower lip. For /w/, round your lips without teeth contact.',
        },
        {
          text: 'Which word pair are minimal pairs (differ by only one sound)?',
          options: [
            '"light" and "right"',
            '"cat" and "dog"',
            '"house" and "building"',
          ],
          answer: '"light" and "right"',
          explanation: '"Light" and "right" differ only in the first consonant (/l/ vs /r/), making them a minimal pair.',
        },
        {
          text: 'How is the "th" in "mother" different from the "th" in "think"?',
          options: [
            '"mother" is voiced (vocal cords vibrate); "think" is voiceless',
            'They are the same sound',
            '"think" is voiced; "mother" is voiceless',
          ],
          answer: '"mother" is voiced (vocal cords vibrate); "think" is voiceless',
          explanation: '"Mother" uses /ð/ (voiced—you feel a vibration in your throat). "Think" uses /θ/ (voiceless—just air).',
        },
        {
          text: 'The vowel in "cat" (/æ/) is different from the vowel in "bed" (/e/) because:',
          options: [
            'Your mouth opens wider for "cat"',
            'Your mouth opens wider for "bed"',
            'They use the same mouth position',
          ],
          answer: 'Your mouth opens wider for "cat"',
          explanation: 'The /æ/ sound requires a wider, lower jaw position than /e/.',
        },
      ],
    },
    {
      title: 'Word Stress Placement',
      type: 'select',
      instructions: 'Choose the correct stress pattern for each word.',
      questions: [
        {
          text: 'Where is the stress in the word "present" when it is a NOUN (a gift)?',
          options: ['PREsent (first syllable)', 'preSENT (second syllable)'],
          answer: 'PREsent (first syllable)',
          explanation: 'Two-syllable nouns usually stress the first syllable: PREsent.',
        },
        {
          text: 'Where is the stress in the word "record" when it is a VERB (to record a song)?',
          options: ['reCORD (second syllable)', 'REcord (first syllable)'],
          answer: 'reCORD (second syllable)',
          explanation: 'Two-syllable verbs usually stress the second syllable: reCORD.',
        },
        {
          text: 'Where is the stress in "education"?',
          options: [
            'eduCAtion (third syllable)',
            'EDucation (first syllable)',
            'educaTION (fourth syllable)',
          ],
          answer: 'eduCAtion (third syllable)',
          explanation: 'The suffix -tion pulls stress to the syllable just before it: eduCAtion.',
        },
        {
          text: 'Where is the stress in "photograph" vs "photography"?',
          options: [
            'PHOtograph / phoTOGraphy',
            'photoGRAPH / PHOtography',
            'PHOtograph / PHOtography',
          ],
          answer: 'PHOtograph / phoTOGraphy',
          explanation: 'Adding -y shifts stress: PHOtograph → phoTOGraphy. The suffix changes the stress pattern.',
        },
      ],
    },
    {
      title: 'Sentence Stress Identification',
      type: 'select',
      instructions: 'Identify which words in each sentence should receive the main stress.',
      questions: [
        {
          text: 'Which words are stressed? "She went to the store yesterday."',
          options: [
            'WENT, STORE, YESTERDAY (content words)',
            'SHE, TO, THE (function words)',
            'Every word is stressed equally',
          ],
          answer: 'WENT, STORE, YESTERDAY (content words)',
          explanation: 'Content words (main verbs, nouns, adverbs) carry the stress. Function words like "she", "to", "the" are quick and soft.',
        },
        {
          text: 'What changes if you stress different words? "I didn\'t say he STOLE it" vs "I didn\'t say HE stole it."',
          options: [
            'The first focuses on the action; the second focuses on the person',
            'Nothing changes—they mean the same thing',
            'The first is a question; the second is a statement',
          ],
          answer: 'The first focuses on the action; the second focuses on the person',
          explanation: 'Shifting stress changes emphasis. Stressing "STOLE" questions the action; stressing "HE" questions the person.',
        },
        {
          text: 'In the sentence "I\'ve been to London," which word is most likely unstressed and reduced to a schwa?',
          options: ['"to" (/tə/)', '"London"', '"been"'],
          answer: '"to" (/tə/)',
          explanation: '"To" is a function word and is typically reduced to /tə/ in natural speech.',
        },
        {
          text: 'If someone stresses EVERY word equally—"I - WENT - TO - THE - PARK"—how does it sound?',
          options: [
            'Robotic and unnatural',
            'Very clear and professional',
            'Like a native speaker',
          ],
          answer: 'Robotic and unnatural',
          explanation: 'English rhythm depends on contrast between stressed and unstressed words. Equal stress sounds unnatural.',
        },
      ],
    },
    {
      title: 'Pronunciation Self-Check Practice',
      type: 'writing',
      instructions: 'Record yourself saying these phrases, then compare with the model. Focus on the specific pronunciation feature indicated.',
      questions: [
        {
          text: 'Say these minimal pairs aloud and notice the difference: "think / sink", "this / dis", "three / tree". Which pair is hardest for you?',
          example: '"Think" and "sink" are hardest for me. I need to remember to place my tongue between my teeth for the "th" sound instead of using the tip of my tongue on my teeth for "s".',
          tip: 'Touch your tongue to your teeth for "th." If you feel air on your tongue tip, you\'re doing it correctly.',
        },
        {
          text: 'Say "I have a PREsent for you" (noun) and "Let me preSENT my idea" (verb). Describe where you feel the stress in each.',
          example: 'In "PREsent" (noun), I feel the emphasis on the first syllable—my voice is louder and higher on "PRE." In "preSENT" (verb), the second syllable feels stronger and longer.',
          tip: 'Tap your hand on the table on the stressed syllable. You should tap on different beats for the noun vs the verb.',
        },
        {
          text: 'Read this sentence with proper intonation: "Do you like coffee↗? I prefer tea↘. But sometimes↘↗... I have juice↗, water↗, or milk↘."',
          example: 'I raised my voice at "coffee" since it\'s a yes/no question. My voice went down at "tea" (a statement). For the list, I used rising tone on "juice" and "water," then falling on "milk" to signal the end.',
          tip: 'Exaggerate the rises and falls at first. It will feel unnatural, but it trains your ear to hear the patterns.',
        },
      ],
    },
  ],
  tips: [
    'Practice 10 minutes daily—short, consistent sessions beat long, infrequent ones',
    'Record yourself and compare to native speakers (podcasts, news, YouTube)',
    'Focus on one sound at a time—don\'t try to fix everything at once',
    'Use a mirror to check tongue and lip position for tricky sounds',
    'Exaggerate sounds at first; you can tone them down once they feel natural',
  ],
  dailyPractice: [
    'Pick 5 minimal pairs and say them aloud 3 times each (e.g., light/right, vest/west)',
    'Read one paragraph from a news article aloud, focusing on word stress',
    'Listen to 2 minutes of English audio and shadow (repeat immediately after the speaker)',
    'Practice the day\'s target sound in 5 real sentences you might use in the exam',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Basic Pronunciation: English vowel and consonant sounds (th, r, l, v/w), word stress rules (nouns vs verbs), sentence stress (content vs function words), intonation patterns (rising/falling). Difficulty 1 — total beginner level.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...basicPronunciationData,
      title: basicPronunciationData.title + ' (Foundations)',
      overview: { ...basicPronunciationData.overview, duration: '15-20 minutes' },
      sections: basicPronunciationData.sections.slice(0, Math.max(2, Math.ceil(basicPronunciationData.sections.length / 2))),
      exercises: basicPronunciationData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...basicPronunciationData,
      title: basicPronunciationData.title + ' (Advanced)',
      overview: { ...basicPronunciationData.overview, duration: '30-35 minutes' },
      sections: basicPronunciationData.sections,
      exercises: basicPronunciationData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(basicPronunciationData.tips || []),
        'For Band 8+: Focus on natural-sounding delivery rather than just accuracy.',
        'Advanced tip: Integrate this skill naturally in timed IELTS speaking practice.',
      ],
    };
  }
  return basicPronunciationData;
}
